// Player avatar face customization state and analytics.

import React from 'react';
import { analyticsService } from '../services/analyticsService';
import { getNextShape } from '../components/avatars/utilities/expressionCycling';
import type { FacialFeature } from '@airhead-games/shared/configs';

interface AvatarFace {
  eyeShape: string;
  mouthShape: string;
}

interface UseAvatarFaceCustomizationArgs {
  initialFace?: AvatarFace;
  roomCode: string | undefined;
}

interface UseAvatarFaceCustomizationResult {
  customFace: AvatarFace;
  cycleExpression: (
    feature: 'eyes' | 'mouth',
    direction: 'prev' | 'next'
  ) => void;
}

const DEFAULT_FACE: AvatarFace = {
  eyeShape: 'sunset',
  mouthShape: 'watermelon',
};

export function usePlayerFaceCustomization({
  initialFace,
  roomCode,
}: UseAvatarFaceCustomizationArgs): UseAvatarFaceCustomizationResult {
  const [customFace, setCustomFace] = React.useState<AvatarFace>(
    initialFace ?? DEFAULT_FACE
  );

  React.useEffect(() => {
    if (initialFace) {
      setCustomFace(initialFace);
    }
  }, [initialFace]);

  const cycleExpression = React.useCallback(
    (feature: 'eyes' | 'mouth', direction: 'prev' | 'next') => {
      const shapeKey = feature === 'eyes' ? 'eyeShape' : 'mouthShape';
      const featureType: FacialFeature = feature === 'eyes' ? 'eye' : 'mouth';
      const currentExpression = customFace[shapeKey];
      const newExpression = getNextShape(
        currentExpression,
        direction,
        featureType
      );

      if (newExpression === currentExpression) return;

      analyticsService.trackButtonClick('Cycle Expression', {
        link_location: 'player_customization',
        feature_type: feature,
        direction,
        room_code: roomCode || 'unknown',
        previous_expression: currentExpression,
        new_expression: newExpression,
      });

      setCustomFace((prev) => ({
        ...prev,
        [shapeKey]: newExpression,
      }));
    },
    [customFace, roomCode]
  );

  return { customFace, cycleExpression };
}
